// Chemin: C:\gestion-copro\src\pages\admin-saas\CoproprietesListPage.tsx
// Liste des copropriétés clientes - Admin SaaS
// Session 54 : Création initiale
// Session 57 : Correction adresse objet vs string
// Session 159 : SÉCURITÉ CRITIQUE — compteur users VIGIE, protection anti-suppression,
//               badges VIGIE vs CS, compteur documents, modal de confirmation renforcée

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  collection, 
  getDocs, 
  query, 
  orderBy,
  where,
  doc,
  deleteDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { 
  Building2, 
  Users, 
  Plus,
  Search,
  MoreVertical,
  ExternalLink,
  Trash2,
  Settings,
  Mail,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MapPin,
  Globe,
  Shield,
  Eye,
  FileText,
  X,
  ShieldAlert
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface AdresseObject {
  rue?: string;
  codePostal?: string;
  ville?: string;
}

interface Copropriete {
  id: string;
  nom: string;
  adresse?: string | AdresseObject;
  ville?: string;
  codePostal?: string;
  nombreLots?: number;
  budgetAnnuel?: number;
  syndic?: string;
  gmailConfigured: boolean;
  membresCS: number;
  coproprietaires: number;        // Sous-collection CS (ancien modèle)
  usersVigie: number;             // Session 159 : Users VIGIE rattachés (collection users)
  usersTotal: number;             // Session 159 : Tous les users rattachés (CS + VIGIE + autres)
  documentsCount: number;         // Session 159 : Nombre de documents dans la GED
  createdAt?: Date;
  updatedAt?: Date;
  type?: string;                  // 'vigie' ou undefined (classique)
}

// ============================================
// HELPERS
// ============================================

/**
 * Formate l'adresse qu'elle soit string ou objet
 */
const formatAdresse = (copro: Copropriete): string => {
  // Si adresse est un objet
  if (copro.adresse && typeof copro.adresse === 'object') {
    const addr = copro.adresse as AdresseObject;
    const parts: string[] = [];
    if (addr.rue) parts.push(addr.rue);
    if (addr.codePostal) parts.push(addr.codePostal);
    if (addr.ville) parts.push(addr.ville);
    return parts.join(', ');
  }
  
  // Si adresse est une string
  if (typeof copro.adresse === 'string') {
    let result = copro.adresse;
    if (copro.codePostal) result += `, ${copro.codePostal}`;
    if (copro.ville) result += ` ${copro.ville}`;
    return result;
  }
  
  // Fallback sur ville/codePostal séparés
  if (copro.ville || copro.codePostal) {
    const parts: string[] = [];
    if (copro.codePostal) parts.push(copro.codePostal);
    if (copro.ville) parts.push(copro.ville);
    return parts.join(' ');
  }
  
  return '';
};

/**
 * Extrait la ville pour la recherche
 */
const getVille = (copro: Copropriete): string => {
  if (copro.adresse && typeof copro.adresse === 'object') {
    return (copro.adresse as AdresseObject).ville || '';
  }
  return copro.ville || '';
};

/**
 * Session 159 : Vérifie si une copropriété est protégée contre la suppression
 * Une copro est protégée si elle a des utilisateurs rattachés OU des documents
 */
const isProtégée = (copro: Copropriete): boolean => {
  return copro.usersTotal > 0 || copro.documentsCount > 0 || copro.membresCS > 0 || copro.coproprietaires > 0;
};

/**
 * Session 159 : Message de protection détaillé
 */
const getProtectionMessage = (copro: Copropriete): string => {
  const raisons: string[] = [];
  if (copro.usersVigie > 0) raisons.push(`${copro.usersVigie} abonné(s) VIGIE`);
  if (copro.usersTotal - copro.usersVigie > 0) raisons.push(`${copro.usersTotal - copro.usersVigie} autre(s) utilisateur(s)`);
  if (copro.membresCS > 0) raisons.push(`${copro.membresCS} membre(s) CS`);
  if (copro.coproprietaires > 0) raisons.push(`${copro.coproprietaires} copropriétaire(s) enregistré(s)`);
  if (copro.documentsCount > 0) raisons.push(`${copro.documentsCount} document(s)`);
  return raisons.join(', ');
};

// ============================================
// COMPOSANT
// ============================================

const CoproprietesListPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [coproprietes, setCoproprietes] = useState<Copropriete[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  // Session 159 : Suppression sécurisée avec modal
  const [deleteModal, setDeleteModal] = useState<Copropriete | null>(null);
  const [deleteTypedName, setDeleteTypedName] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Charger les copropriétés
  const loadCoproprietes = async () => {
    try {
      const coproQuery = query(
        collection(db, 'coproprietes'),
        orderBy('nom', 'asc')
      );
      const snapshot = await getDocs(coproQuery);
      
      // Session 159 : Charger TOUS les users une seule fois (performance)
      const usersSnap = await getDocs(collection(db, 'users'));
      const allUsers = usersSnap.docs.map(d => ({
        id: d.id,
        role: d.data().role as string,
        coproprieteIds: (d.data().coproprieteIds || []) as string[],
        status: d.data().status as string
      }));

      const coproList: Copropriete[] = [];
      
      for (const coproDoc of snapshot.docs) {
        const data = coproDoc.data();
        const coproId = coproDoc.id;

        // Compter les membres CS (sous-collection)
        let membresCSCount = 0;
        try {
          const membresCSSnap = await getDocs(
            collection(db, `coproprietes/${coproId}/membresCS`)
          );
          membresCSCount = membresCSSnap.size;
        } catch (e) {
          // Sous-collection peut ne pas exister
        }

        // Compter les copropriétaires (sous-collection CS)
        let coproprietairesCount = 0;
        try {
          const coproprietairesSnap = await getDocs(
            collection(db, `coproprietes/${coproId}/coproprietaires`)
          );
          coproprietairesCount = coproprietairesSnap.size;
        } catch (e) {
          // Sous-collection peut ne pas exister
        }

        // Session 159 : Compter les users rattachés via coproprieteIds (VIGIE + autres)
        const usersRattaches = allUsers.filter(u => u.coproprieteIds.includes(coproId));
        const usersVigieCount = usersRattaches.filter(u => u.role === 'vigie').length;
        const usersTotalCount = usersRattaches.length;

        // Session 159 : Compter les documents (sous-collection)
        let documentsCount = 0;
        try {
          const docsSnap = await getDocs(
            collection(db, `coproprietes/${coproId}/documents`)
          );
          documentsCount = docsSnap.size;
        } catch (e) {
          // Sous-collection peut ne pas exister
        }

        // Vérifier si Gmail est configuré
        const gmailConfigured = !!(data.gmailTokens?.access_token);

        coproList.push({
          id: coproId,
          nom: data.nom || 'Sans nom',
          adresse: data.adresse,
          ville: data.ville,
          codePostal: data.codePostal,
          nombreLots: data.nombreLots,
          budgetAnnuel: data.budgetAnnuel,
          syndic: data.syndic,
          gmailConfigured,
          membresCS: membresCSCount,
          coproprietaires: coproprietairesCount,
          usersVigie: usersVigieCount,
          usersTotal: usersTotalCount,
          documentsCount,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          type: data.type || undefined
        });
      }

      setCoproprietes(coproList);
    } catch (error) {
      console.error('Erreur chargement coproprietes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoproprietes();
  }, []);

  // Fermer le menu si clic ailleurs
  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Filtrer les copropriétés
  const filteredCoproprietes = coproprietes.filter(copro => {
    const searchLower = searchTerm.toLowerCase();
    const adresseStr = formatAdresse(copro).toLowerCase();
    const ville = getVille(copro).toLowerCase();
    
    return copro.nom.toLowerCase().includes(searchLower) ||
           ville.includes(searchLower) ||
           adresseStr.includes(searchLower);
  });

  // Session 159 : Suppression sécurisée — ouvre la modal
  const handleDeleteRequest = (copro: Copropriete) => {
    setMenuOpen(null);
    if (isProtégée(copro)) {
      // Copro protégée — affiche la modal d'information (pas de suppression possible)
      setDeleteModal(copro);
      setDeleteTypedName('');
    } else {
      // Copro vide — affiche la modal de confirmation avec saisie du nom
      setDeleteModal(copro);
      setDeleteTypedName('');
    }
  };

  // Session 159 : Exécuter la suppression (uniquement si copro vide ET nom saisi correctement)
  const handleDeleteConfirm = async () => {
    if (!deleteModal) return;
    if (isProtégée(deleteModal)) return; // Sécurité supplémentaire
    if (deleteTypedName.trim().toLowerCase() !== deleteModal.nom.trim().toLowerCase()) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'coproprietes', deleteModal.id));
      setCoproprietes(prev => prev.filter(c => c.id !== deleteModal.id));
      setDeleteModal(null);
      setDeleteTypedName('');
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Copropriétés
          </h1>
          <p className="text-gray-500 mt-1">
            {coproprietes.length} copropriété{coproprietes.length > 1 ? 's' : ''} enregistrée{coproprietes.length > 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/admin-saas/coproprietes/nouvelle"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <Plus size={18} />
          <span>Nouvelle copropriété</span>
        </Link>
      </div>

      {/* Barre de recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Rechercher par nom, ville ou adresse..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        />
      </div>

      {/* Liste des copropriétés */}
      {filteredCoproprietes.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
          <Building2 className="mx-auto text-gray-300 mb-4" size={64} />
          {searchTerm ? (
            <>
              <p className="text-lg font-medium text-gray-900">Aucun résultat</p>
              <p className="text-gray-500 mt-1">
                Aucune copropriété ne correspond à "{searchTerm}"
              </p>
            </>
          ) : (
            <>
              <p className="text-lg font-medium text-gray-900">Aucune copropriété</p>
              <p className="text-gray-500 mt-1">
                Commencez par créer votre première copropriété cliente
              </p>
              <Link
                to="/admin-saas/coproprietes/nouvelle"
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Plus size={18} />
                <span>Créer une copropriété</span>
              </Link>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredCoproprietes.map((copro) => {
            const adresseFormatee = formatAdresse(copro);
            const isVigieType = copro.type === 'vigie' || copro.id.startsWith('vigie-');
            const accederUrl = isVigieType ? `/vigie?copro=${copro.id}` : `/admin?copro=${copro.id}`;
            const protegee = isProtégée(copro);
            
            return (
              <div
                key={copro.id}
                className={`bg-white rounded-xl shadow-sm border hover:shadow-md transition-shadow ${
                  protegee ? 'border-emerald-200' : 'border-gray-100'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    {/* Infos principales */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          protegee ? 'bg-emerald-100' : 'bg-gray-100'
                        }`}>
                          <Building2 className={protegee ? 'text-emerald-600' : 'text-gray-400'} size={24} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-gray-900 text-lg">
                              {copro.nom}
                            </h3>
                            {/* Session 159 : Badge protection */}
                            {protegee && (
                              <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
                                <Shield size={12} />
                                Protégée
                              </span>
                            )}
                          </div>
                          {adresseFormatee && (
                            <p className="text-sm text-gray-500 flex items-center gap-1">
                              <MapPin size={14} />
                              {adresseFormatee}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Indicateurs */}
                      <div className="mt-4 flex flex-wrap gap-3">
                        {/* Gmail */}
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                          copro.gmailConfigured 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-amber-100 text-amber-700'
                        }`}>
                          <Mail size={14} />
                          <span>{copro.gmailConfigured ? 'Gmail configuré' : 'Gmail non configuré'}</span>
                        </div>

                        {/* Session 159 : Users VIGIE rattachés — badge distinct et prioritaire */}
                        {copro.usersVigie > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-700 font-medium">
                            <Eye size={14} />
                            <span>{copro.usersVigie} abonne{copro.usersVigie > 1 ? 's' : ''} VIGIE</span>
                          </div>
                        )}

                        {/* Session 159 : Autres users rattachés (non-VIGIE) */}
                        {(copro.usersTotal - copro.usersVigie) > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-purple-100 text-purple-700">
                            <Users size={14} />
                            <span>{copro.usersTotal - copro.usersVigie} autre{(copro.usersTotal - copro.usersVigie) > 1 ? 's' : ''} utilisateur{(copro.usersTotal - copro.usersVigie) > 1 ? 's' : ''}</span>
                          </div>
                        )}

                        {/* Membres CS */}
                        {copro.membresCS > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-emerald-100 text-emerald-700">
                            <Users size={14} />
                            <span>{copro.membresCS} membre{copro.membresCS > 1 ? 's' : ''} CS</span>
                          </div>
                        )}

                        {/* Copropriétaires sous-collection */}
                        {copro.coproprietaires > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700">
                            <Users size={14} />
                            <span>{copro.coproprietaires} coproprietaire{copro.coproprietaires > 1 ? 's' : ''}</span>
                          </div>
                        )}

                        {/* Session 159 : Documents */}
                        {copro.documentsCount > 0 && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700">
                            <FileText size={14} />
                            <span>{copro.documentsCount} document{copro.documentsCount > 1 ? 's' : ''}</span>
                          </div>
                        )}

                        {/* Lots */}
                        {copro.nombreLots && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-gray-100 text-gray-700">
                            <span>{copro.nombreLots} lots</span>
                          </div>
                        )}

                        {/* Session 159 : Badge "vide" pour les copro sans rien */}
                        {!protegee && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-red-50 text-red-500">
                            <AlertTriangle size={14} />
                            <span>Vide — supprimable</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Menu actions */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(menuOpen === copro.id ? null : copro.id);
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical size={20} className="text-gray-400" />
                      </button>

                      {menuOpen === copro.id && (
                        <div 
                          className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            to={accederUrl}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink size={16} />
                            <span>Accéder</span>
                          </Link>
                          <Link
                            to={`/admin/copropriete?copro=${copro.id}`}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Settings size={16} />
                            <span>Paramètres</span>
                          </Link>
                          <Link
                            to={`/admin-saas/coproprietes/${copro.id}/portail`}
                            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Globe size={16} />
                            <span>Configurer portail</span>
                          </Link>
                          <hr className="my-1 border-gray-100" />
                          {/* Session 159 : Bouton suppression conditionnel */}
                          {protegee ? (
                            <button
                              onClick={() => handleDeleteRequest(copro)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-gray-400 cursor-not-allowed"
                              title="Suppression impossible — cette copropriété a des données"
                            >
                              <Shield size={16} />
                              <span>Suppression bloquée</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleDeleteRequest(copro)}
                              className="w-full flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 size={16} />
                              <span>Supprimer</span>
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pied de carte */}
                  <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      {copro.syndic && (
                        <span>Syndic : {copro.syndic}</span>
                      )}
                    </div>
                    <Link
                      to={accederUrl}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors text-sm font-medium"
                    >
                      <span>Accéder à l'espace</span>
                      <ExternalLink size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Statistiques globales */}
      <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
        <h3 className="font-semibold text-slate-700 mb-3">Résumé</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-slate-900">{coproprietes.length}</p>
            <p className="text-sm text-slate-500">Copropriétés</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {coproprietes.reduce((sum, c) => sum + c.usersVigie, 0)}
            </p>
            <p className="text-sm text-slate-500">Abonnés VIGIE</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">
              {coproprietes.reduce((sum, c) => sum + c.membresCS, 0)}
            </p>
            <p className="text-sm text-slate-500">Membres CS</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900">
              {coproprietes.reduce((sum, c) => sum + c.documentsCount, 0)}
            </p>
            <p className="text-sm text-slate-500">Documents</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-emerald-600">
              {coproprietes.filter(c => isProtégée(c)).length}/{coproprietes.length}
            </p>
            <p className="text-sm text-slate-500">Protégées</p>
          </div>
        </div>
      </div>

      {/* Session 159 : Modal de suppression sécurisée */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {isProtégée(deleteModal) ? (
              /* Modal BLOQUÉE — copro avec données */
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <ShieldAlert className="text-red-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Suppression impossible</h3>
                    <p className="text-sm text-gray-500">{deleteModal.nom}</p>
                  </div>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    Cette copropriété contient des données actives :
                  </p>
                  <p className="text-sm text-red-700">
                    {getProtectionMessage(deleteModal)}
                  </p>
                </div>

                <p className="text-sm text-gray-600 mb-6">
                  Pour supprimer cette copropriété, vous devez d'abord supprimer ou migrer toutes les données rattachées (utilisateurs, documents, membres).
                </p>

                <button
                  onClick={() => { setDeleteModal(null); setDeleteTypedName(''); }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Compris
                </button>
              </div>
            ) : (
              /* Modal CONFIRMATION — copro vide, suppression possible */
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="text-amber-600" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
                    <p className="text-sm text-gray-500">{deleteModal.nom}</p>
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Cette copropriété est vide (aucun utilisateur, aucun document). La suppression est irréversible.
                </p>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tapez le nom exact pour confirmer :
                  </label>
                  <input
                    type="text"
                    value={deleteTypedName}
                    onChange={(e) => setDeleteTypedName(e.target.value)}
                    placeholder={deleteModal.nom}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setDeleteModal(null); setDeleteTypedName(''); }}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleDeleteConfirm}
                    disabled={
                      deleting || 
                      deleteTypedName.trim().toLowerCase() !== deleteModal.nom.trim().toLowerCase()
                    }
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      deleteTypedName.trim().toLowerCase() === deleteModal.nom.trim().toLowerCase() && !deleting
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-red-200 text-red-400 cursor-not-allowed'
                    }`}
                  >
                    {deleting ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        <span>Supprimer</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoproprietesListPage;
